import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useState } from "react";
import { X } from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import FloatingADUWizard from "@/components/FloatingADUWizard";
import CanonicalTag from "@/components/CanonicalTag";
import laundryMudroomImage from "@/assets/laundry-mudroom-adu.jpg";

const HowToUseADUBackyardLaundryOrMudroom = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-backyard-laundry-or-mudroom" />
      <Navigation />
        {/* Hero Section */}
        <section className="relative overflow-hidden py-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-8">
                <h1 className="text-4xl lg:text-6xl font-bold leading-tight" style={{ color: '#E4683B' }}>
                  Bring Functionality Outdoors
                </h1>
                <p className="text-xl text-white leading-relaxed">
                  Create a dedicated backyard laundry/mudroom ADU to keep chores out of your home.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button 
                    size="lg" 
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={openCalendlyModal}
                  >
                    📅 Book & Schedule
                  </Button>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={laundryMudroomImage} 
                  alt="Modern laundry and mudroom ADU with organized storage and appliances" 
                  className="rounded-lg shadow-2xl w-full h-auto"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Designed for Organization Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Designed for Organization
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Washer/Dryer Combo</h3>
                  <p className="text-muted-foreground">Efficient appliance setup with integrated folding counter for streamlined laundry workflow.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Smart Storage</h3>
                  <p className="text-muted-foreground">Custom cabinets, benches, and wall hooks to organize cleaning supplies and outdoor gear.</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold mb-4 text-foreground">Durable Design</h3>
                  <p className="text-muted-foreground">Water-resistant flooring and utility sink designed to handle messy tasks and heavy use.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Everyday Efficiency */}
        <section className="py-16 px-4 bg-muted/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>
              Everyday Efficiency
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-left">
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Saves Indoor Space</h3>
                <p className="text-muted-foreground">Frees up valuable interior square footage for living areas and storage.</p>
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-4 text-foreground">Reduces Noise & Clutter</h3>
                <p className="text-muted-foreground">Perfect for managing pets, kids' gear, and messy outdoor activities away from main home.</p>
              </div>
            </div>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-white text-[#E4683B] hover:bg-white/90"
                onClick={() => window.location.href = '/landing'}
              >
                📐 What fits my lot?
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/price-roi-report', '_blank')}
              >
                💰 Get Price & ROI Report
              </Button>
            </div>
          </div>
        </section>

        {/* Property Value Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>
              Boost Property Value
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Functional Upgrade</h3>
                <p className="text-muted-foreground">Practical amenity that appeals to homebuyers seeking organized, efficient living solutions.</p>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-4 text-foreground">Added Square Footage</h3>
                <p className="text-muted-foreground">Increases total livable and functional space, directly contributing to property value.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 px-4 bg-primary/10">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold mb-8 text-white">
              Build a business that grows from home—without being in your home.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('/our-work', '_blank')}
              >
                🏡 Our Portfolio
              </Button>
              <Button 
                size="lg"
                className="bg-[#E4683B] text-white hover:bg-[#E4683B]/90"
                onClick={() => window.open('https://udaadu.com/faq', '_blank')}
              >
                ❓ Our FAQ
              </Button>
            </div>
          </div>
        </section>

        {/* Calendly Modal */}
        {showCalendly && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="relative w-full max-w-2xl h-[70vh] bg-white rounded-lg overflow-hidden shadow-xl">
              <button 
                onClick={closeCalendlyModal} 
                className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
              >
                <X className="h-6 w-6 text-gray-600" />
              </button>
              <iframe 
                src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
                width="100%" 
                height="100%" 
                frameBorder="0" 
                title="Schedule a meeting" 
              />
            </div>
          </div>
        )}
        
        <Footer />
        <FloatingADUWizard />
      </div>
  );
};

export default HowToUseADUBackyardLaundryOrMudroom;